// BookTickets/stages/DateAndTime/BookingList.tsx
'use client'

import { useCallback, useMemo } from 'react'
import cn from 'classnames'
import { useTranslations } from 'next-intl'
import { isFuture, format as formatDate } from 'date-fns'
import { enGB, es, fr, pt } from 'date-fns/locale'
import { Locale } from 'date-fns'
import { type LocaleKey } from '@/src/i18n/routing'

import styles from './index.module.scss'

import { useBookingActionsCtx, useBookingState } from '@booking/state/booking.context'
import type { Session } from '@booking/types/booking'

import { calcSessionTotal, ticketCount } from '@booking/lib/cart'
import { formatPrice } from '@booking/lib/money'

import trackGA4 from '@booking/services/trackingGa4'
import { format as formatInLisbonTz } from '@booking/lib/date'
import { useBookingExternal } from '@/src/components/BookTickets/state/external.context'

type BookingListProps = {
  onBook: (session: Session) => void
  date: Date
  minDate?: Date
  maxDate?: Date
  loading: boolean
  sessions: Session[]
  /**
   * Recommended: let the parent own date changes.
   * If you don’t pass it, arrows will be disabled (instead of console.warn + doing nothing).
   */
  onChangeDate?: (nextDate: Date) => void
}

function dateFnsLocale(locale: LocaleKey) {
  const map: Record<LocaleKey, Locale> = { en: enGB, pt: pt, fr, es }
  return map[locale as LocaleKey] ?? enGB
}

function getDateFormat(locale: LocaleKey) {
  return locale === 'pt' ? "dd 'de' MMM" : 'EEE dd MMM'
}

export default function BookingList({
  onBook,
  date,
  minDate,
  //maxDate, // unused for now, kept for parity
  loading,
  sessions,
  onChangeDate,
}: BookingListProps) {
  const t = useTranslations('booking')
  const { locale } = useBookingExternal()

  const state = useBookingState()
  const { dispatch } = useBookingActionsCtx()

  const formattedHeaderDate = useMemo(() => {
    return formatDate(date, getDateFormat(locale), { locale: dateFnsLocale(locale) })
  }, [date, locale])

  const isMin = useCallback((d: Date) => Boolean(minDate && d <= minDate), [minDate])

  const canGoPrev = useMemo(() => {
    return Boolean(onChangeDate) && isFuture(date) && !isMin(date)
  }, [date, isMin, onChangeDate])

  const canGoNext = useMemo(() => {
    // If maxDate is ever needed, include it here
    return Boolean(onChangeDate)
  }, [onChangeDate])

  const previousDay = useCallback(() => {
    if (!onChangeDate) return
    dispatch({ type: 'SET_SESSION', session: null })
    onChangeDate(new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1))
  }, [date, dispatch, onChangeDate])

  const nextDay = useCallback(() => {
    if (!onChangeDate) return
    dispatch({ type: 'SET_SESSION', session: null })
    onChangeDate(new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1))
  }, [date, dispatch, onChangeDate])

  const onSelect = useCallback(
    (session: Session) => {
      onBook(session)
      dispatch({ type: 'SET_SESSION', session })
      trackGA4.selectSession?.(session)
    },
    [dispatch, onBook],
  )

  const showNoneText = t('ticket.selectDate.unavailable')

  return (
    <div className={cn(styles.wrapper, false && styles.special)}>
      <div className={cn(styles.header)}>
        <button
          type="button"
          className={cn(styles.arrow, styles.leftArrow, !canGoPrev && styles.disabled)}
          onClick={previousDay}
          disabled={!canGoPrev}
          aria-label={t('ticket.selectDate.previousDay', { default: 'Previous day' })}
        >
          ‹
        </button>

        <span>{formattedHeaderDate}</span>

        <button
          type="button"
          className={cn(styles.arrow, styles.rightArrow, !canGoNext && styles.disabled)}
          onClick={nextDay}
          disabled={!canGoNext}
          aria-label={t('ticket.selectDate.nextDay', { default: 'Next day' })}
        >
          ›
        </button>
      </div>

      {sessions.length ? (
        <div className={cn(styles.listWrapper)} data-id="booking-list">
          <div className={cn(styles.list, loading && styles.loading, styles.anim)}>
            {sessions.map((item) => (
              <TimeSlot
                key={item.id}
                session={item}
                onSelect={onSelect}
                selected={Boolean(state.session && state.session.id === item.id)}
                locale={locale}
              />
            ))}
          </div>
        </div>
      ) : (
        <div className={cn(styles.none)}>{showNoneText}</div>
      )}
    </div>
  )
}

type Availability = 'full' | 'amount' | 'open'

type TimeSlotProps = {
  session: Session
  selected: boolean
  onSelect: (session: Session) => void
  locale: string
}

function TimeSlot({ session, selected, onSelect, locale }: TimeSlotProps) {
  const t = useTranslations('booking')
  const state = useBookingState()

  const count = useMemo(() => ticketCount(state.quantities), [state.quantities])

  const availability: Availability = useMemo(() => {
    const availableSeats = session.vacancies
    if (availableSeats === 0) return 'full'
    if (availableSeats == null) return 'open'
    if (availableSeats < count) return 'amount'
    return 'open'
  }, [count, session.vacancies])

  const isDisabled = availability === 'full' || availability === 'amount'

  const unavailableText = useMemo(() => {
    if (availability === 'full') return t('ticket.selectDate.soldOut')
    if (availability === 'amount') return t('ticket.selectDate.soldOut')
    return ''
  }, [availability, t])

  const timeText = useMemo(() => {
    return formatInLisbonTz(new Date(session.dateTime), locale === 'en' ? 'HH:mm aaa' : 'HH:mm')
  }, [locale, session.dateTime])

  const timeTextBasic = useMemo(() => {
    return formatInLisbonTz(new Date(session.dateTime), 'HH:mm')
  }, [session.dateTime])

  const totalText = useMemo(() => {
    return formatPrice(calcSessionTotal({ session: session, quantities: state.quantities }))
  }, [state.quantities, session])

  const handleClick = useCallback(() => onSelect(session), [onSelect, session])

  if (isDisabled) {
    return (
      <div className={cn(styles.slot, styles.disabled)} data-time={timeTextBasic}>
        <div className={cn(styles.time)}>{timeText}</div>
        <div className={cn(styles.text)}>{unavailableText}</div>
      </div>
    )
  }

  return (
    <button
      type="button"
      className={cn(styles.slot, selected && styles.selected, styles.open)}
      onClick={handleClick}
      aria-pressed={selected}
      data-time={timeTextBasic}
    >
      <div className={cn(styles.time)}>{timeText}</div>
      <div className={cn(styles.total)}>Total {totalText}</div>
    </button>
  )
}
